import { useEffect } from 'react';
import { RouterProvider } from '@tanstack/react-router';
import dayjs from 'dayjs';
import { QueryClientProvider } from 'react-query';
import { logTimeToRedirectToAuth } from '@frontend/auth';
import { startWeaveSession, registerTokenStorageEventListener } from '@frontend/auth-helpers';
import { useRemoveAppLoader } from '@frontend/document';
import appConfig, { getInitialParams, isLocalDeployment } from '@frontend/env';
import { ErrorBoundary } from '@frontend/error-boundary';
import { http } from '@frontend/fetch';
import { queryClient } from '@frontend/query-client';
import { useWeTags } from '@frontend/scope';
import { InterRouterProvider } from '@frontend/settings-routing';
import { shell } from '@frontend/shell-utils';
import { registerTabSessionId } from '@frontend/tab-management';
import { TracerModal } from '@frontend/tracer-modal';
import { getNavigationStartTime, usePageLoadSpanMarker } from '@frontend/tracking';
import { ThemeProvider, AlertsProvider, configureModalSettings, Modal } from '@frontend/design-system';
import '@design-system/tokens';
import { TOP_BAR_HEIGHT } from './constants';
import { AppProvider } from './providers/app.provider';
import { RoutesProvider } from './providers/routes.provider';
import { initializeRouter } from './router';
import { getDayJSLocale } from './utils/dayjs-locales';

// Conditional loading on ENV, is set in the loader function
if (import.meta.env.MODE === 'production' && !isLocalDeployment()) {
  import('./third-party-scripts').then(({ loadThirdPartyScripts }) => {
    loadThirdPartyScripts();
  });
} else {
  console.warn('Third party scripts are not loaded because the app is not running in production.');
}

const router = initializeRouter();

declare global {
  interface Window {
    Cypress?: any;
  }
}

registerTokenStorageEventListener();
startWeaveSession();
registerTabSessionId();

const hasShellThemeEnabled = shell?.isShell && shell?.featureAvailability?.has('shell-theme');
const hasNewShellTitleBar = shell?.isShell && shell?.featureAvailability?.has('shell-title-bar-view');
const hasLegacyShellTitleBar = hasShellThemeEnabled && !hasNewShellTitleBar;

export default function Root() {
  // This is used to mark the root component as mounted in the Sentry waterfall trace
  // Set the startTime to just after the DOM content is loaded (+1 so it shows after the browser.domContentLoadedEvent in Sentry)
  usePageLoadSpanMarker('Root mounted', 'ui.react.mounted', performance.timing.domContentLoadedEventEnd + 1);
  // For this timing, set the startTime to when the browser starts handling a request which gives us the complete time till the user can do something in the app
  usePageLoadSpanMarker('Weave app mounted', 'ui.weaveapp.mounted', getNavigationStartTime());
  const newAlert = useWeTags('new-alert-design');

  useEffect(() => {
    // delay it because it uses pendo which is lazy-loaded
    setTimeout(() => {
      logTimeToRedirectToAuth();
    }, 2000);
  }, []);

  console.log('Weave Assets Version: ', appConfig.VERSION);
  console.log('Weave Commit Hash: ', appConfig.COMMITHASH);

  if (shell?.isShell) {
    console.log('Weave Shell Version: ', shell?.version);
  }

  useEffect(() => {
    getDayJSLocale().then((locale) => {
      dayjs.locale(locale);
    });
  }, []);

  useEffect(() => {
    configureModalSettings({
      mountTarget: () => {
        return document.getElementById('vertical-slide-modal-backdrop')?.contains(document.activeElement)
          ? '#vertical-slide-modal-backdrop'
          : undefined;
      },
    });
  }, []);

  if (!http.baseUrl) {
    const { backendApi } = getInitialParams();
    http.setBaseUrl(backendApi);
  }

  useRemoveAppLoader();
  return (
    <>
      <ErrorBoundary
        tier='app'
        feature='weave.root'
        team='frontend-ops'
        onError={(error, errorInfo) => {
          console.error('ErrorBoundary caught an error:', error, errorInfo);
        }}
        fallback={({ error }) => {
          return <div>An error occurred: {(error as Error).message}</div>;
        }}
      >
        <AppProvider>
          <QueryClientProvider client={queryClient}>
            <ThemeProvider
              includeEmotionTheme
              skipAnimation={!!window?.Cypress}
              heightOffset={hasLegacyShellTitleBar ? 55 : 0}
            >
              <RoutesProvider queryClient={queryClient}>
                <InterRouterProvider>
                  <Modal.AppConfigProvider topOffset={TOP_BAR_HEIGHT}>
                    <AlertsProvider variant={newAlert ? 'experimental' : 'default'}>
                      <RouterProvider router={router} />
                    </AlertsProvider>
                  </Modal.AppConfigProvider>
                </InterRouterProvider>
                <TracerModal />
              </RoutesProvider>
            </ThemeProvider>
          </QueryClientProvider>
        </AppProvider>
      </ErrorBoundary>
    </>
  );
}
